<script setup lang="ts">

import CustomButton from "@/components/Button/Button.vue";
import CustomIncrement from "@/components/CustomIncrement/CustomIncrement.vue";
import {reactive} from "vue";
import {useI18n} from "vue-i18n";
import {useHomepageStore} from "@/stores/homepageStore";
import * as Passengers from './composables/passengers';
import {trackEvent} from "@/services/event.service";

const {t} = useI18n();
const homepageStore = useHomepageStore()
const usePassengers = reactive(Passengers.usePassengers())


const emit = defineEmits<{
  'on-search': [void],
  'on-recommend': [void],
}>();
const props = defineProps({
  tabindex: Number,
  canSelectSkipper: {
    type: Boolean,
    default: false,
  },
  isGulet: {
    type: Boolean,
    default: false,
  },
});

function clickedParent() {
  homepageStore.current = 'passenger';
}

const onApply = ()=>{
  homepageStore.selections.skipper = usePassengers.skipper;
  homepageStore.selections.passengers = usePassengers.passengers;
  homepageStore.selections.cabins = usePassengers.cabins;

}
const onSearch = ()=> {
  trackEvent('cosmosy.ga.mainSearch.yachtSearch', {event_label: 'Yacht Search'});
  onApply();
  emit('on-search')
}

const onRecommend = ()=>{
  trackEvent('cosmosy.ga.mainSearch.expertSelection', {event_label: 'Expert Selection' });
  onApply();
  emit('on-recommend')
}

</script>

<template>
  <div class="relative w-1/2 h-full" :tabindex="props.tabindex" >
    <slot name="display" :passengers="usePassengers" :clickedParent="clickedParent">
      <div id="passenger-cabins" :class="`flex flex-col justify-between h-full py-4.5 pl-7.5 cursor-pointer`"
           @click="clickedParent">
        <div class="text-black/65 flex flex-row items-center gap-2  text-xs">
          <img src="@/assets/icons/svg/Guest_16x16.svg" alt="Guests">
          <span>{{ t('booking.passengersAndCabinsTitle') }}</span>
        </div>
        <div class="text-black responsive-title-5 flex flex-row items-center">
          <div class="mr-6">{{ usePassengers.text }}</div>
        </div>
      </div>

    </slot>
    <div
         v-if="homepageStore.current == 'passenger'"
         class="shadow-2xl absolute top-full h-fit left-0 w-full bg-white border-t-2 border-primary-blue z-20 rounded-b-11xl">
      <div class="flex flex-col gap-3 justify-center items-center py-4.5 px-5 ">
        <div class="flex items-center justify-between w-full">
          <div class="responsive-body-small">{{ t('booking.passengersLabel') }}</div>
          <CustomIncrement :value="usePassengers.passengers"
                           @increment="usePassengers.incrementPassenger"
                           @decrement="usePassengers.decrementPassenger"/>
        </div>
        <div class="flex items-center justify-between w-full">
          <div class="responsive-body-small">{{ t('booking.cabinsLabel') }}</div>
          <CustomIncrement :value="usePassengers.cabins"
                           @increment="usePassengers.incrementCabin"
                           @decrement="usePassengers.decrementCabin"/>
        </div>
<!--        Reverted this back to using store   -->
        <div v-if="canSelectSkipper" class="w-full flex flex-row justify-between items-center">
          <div class="flex flex-col items-start justify-between w-8/12 gap-y-1">
            <div class="responsive-body-small">{{ t('booking.skipperLabel') }}</div>
            <div class="responsive-body-xsmall text-black/40">{{ t('booking.skipperInfo') }}</div>
          </div>
          <VSwitch inset v-model="usePassengers.skipper" base-color="grey" color="black" />
        </div>
        <div v-if="isGulet" class="w-full flex flex-row justify-between items-end">
          <div class="responsive-body-xsmall text-black/40">{{ t('booking.noSkipperInfo') }}</div>
        </div>
      </div>
      <div
          :class="['from-gradients-ghostWhite to-gradients-aliceBlue bg-gradient-to-tr flex items-end w-full py-4.5 px-5 rounded-b-11xl', 
              homepageStore.canRecommend ? 'justify-between' : 'justify-end']">

        <CustomButton
            v-if="homepageStore.canRecommend"
            @click="onRecommend"
            :outlined="false"
            type="black"
            :rounded="true"
            class="hover:bg-black hover:text-white hover:border-black">
          {{ t('search.offersButtonText') }}
        </CustomButton>

        <CustomButton
            v-if="homepageStore.canSearch"
            @click="onSearch"
            :outlined="false"
            type="black"
            :rounded="true"
            class="hover:bg-black hover:text-white hover:border-black">
          {{ t('search.buttonText') }}
        </CustomButton>
      </div>
    </div>
  </div>
</template>

<style scoped>
:deep(.v-switch__track) {
  opacity: 1;
}
</style>
